import React, {useState, useCallback} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {withRouter, useRouteUnmountEffect} from '@computerrock/formation-router';
import {useTranslate} from '@computerrock/formation-i18n';
import {useStyles, ContentBlock, ContentItem, Panel, calendarIcon} from '@ace-de/ui-components';
import {DateField} from '@ace-de/ui-components/form';
import {persistenceStates, efServiceCaseTypes} from '@ace-de/eua-entity-types';
import * as serviceAssignmentSelectors from '../service-assignments/serviceAssignmentSelectors';
import * as serviceCaseSelectors from '../service-cases/serviceCaseSelectors';
import * as saspActionTypes from './saspActionTypes';
import * as serviceAssignmentActionTypes from '../service-assignments/serviceAssignmentActionTypes';
import AlternativeServiceProviderPanel from '../service-assignments/ui-elements/AlternativeServiceProviderPanel';
import ServiceProviderPanel from '../service-assignments/ui-elements/ServiceProviderPanel';
import serviceAssignmentPartnerDisabledStateStatuses from '../service-assignments/serviceAssignmentPartnerDisabledStateStatuses';

const SASPServiceProviderTab = props => {
    const {cx} = useStyles();
    const {createTranslateShorthand, activeLocale} = useTranslate();
    const translateTab = createTranslateShorthand('sasp_service_provider_tab');
    const {serviceAssignment, serviceCase, serviceProviders} = props;
    const {submitSASPServiceProviderForm, resetServiceProviderList} = props;
    const [isEditModeActive, setIsEditModeActive] = useState(!!serviceAssignment?.acePartner?.isManuallyAdded);
    const [pickupDate, setPickupDate] = useState(serviceAssignment.pickupDate);
    const [selectedServiceProviderId, setSelectedServiceProviderId] = useState(
        serviceAssignment?.acePartner && !serviceAssignment?.acePartner?.isManuallyAdded
            ? serviceAssignment.acePartner?.id : null,
    );
    const [isDateInvalid, setIsDateInvalid] = useState(false);

    const handleOnSelectServiceProvider = id => {
        if (id && typeof id === 'string') {
            setSelectedServiceProviderId(id);
        }
    };

    const handleOnTabChange = useCallback(formValues => {
        submitSASPServiceProviderForm({
            serviceAssignmentLineNo: serviceAssignment.lineNo,
            serviceCaseId: serviceAssignment.serviceCaseId,
            serviceAssignmentData: {
                ...(!isDateInvalid && {pickupDate}),
                ...(!isEditModeActive
                    ? {
                        acePartner: selectedServiceProviderId
                            ? {
                                ...serviceProviders[selectedServiceProviderId],
                                location: {
                                    ...(!!serviceProviders[selectedServiceProviderId]?.location
                                            && serviceProviders[selectedServiceProviderId].location),
                                    ...(!!serviceProviders[selectedServiceProviderId]?.address
                                            && serviceProviders[selectedServiceProviderId].address),
                                },
                                contactDetails: null,
                            }
                            : null,
                    }
                    : {}
                ),
            },
        });
    }, [
        isEditModeActive,
        serviceAssignment.lineNo,
        serviceAssignment.serviceCaseId,
        selectedServiceProviderId,
        serviceProviders,
        pickupDate,
        submitSASPServiceProviderForm,
        isDateInvalid,
    ]);

    useRouteUnmountEffect(() => {
        if (serviceAssignment.persistenceState === persistenceStates.READY) {
            resetServiceProviderList();
        }
        handleOnTabChange();
    }, [handleOnTabChange, serviceAssignment.persistenceState, resetServiceProviderList]);

    const handleOnToggleSwitch = () => {
        if (!isEditModeActive && selectedServiceProviderId !== null) {
            setSelectedServiceProviderId('');
        }
        setIsEditModeActive(!isEditModeActive);
    };

    // if no service assignment or serviceCase, don't render
    if (!serviceAssignment || !serviceCase) return null;

    const {assignmentStatus} = serviceAssignment;
    const isPartnerChangingDisabled = serviceAssignmentPartnerDisabledStateStatuses.includes(assignmentStatus)
        && serviceCase.caseType === efServiceCaseTypes.VEHICLE;

    return (
        <ContentBlock
            className={cx([
                'global!ace-u-height--full',
                'global!ace-u-max-height--full',
                'global!ace-u-flex--align-stretch',
            ])}
        >
            <ContentItem
                className={cx([
                    'ace-c-content-item--span-3',
                    'global!ace-u-height--full',
                    'global!ace-u-max-height--full',
                ])}
            >
                <div
                    className={cx([
                        'global!ace-u-flex',
                        'global!ace-u-flex--direction-column',
                        'global!ace-u-height--full',
                        'global!ace-u-max-height--full',
                    ])}
                >
                    <Panel title={translateTab('pickup_date_panel.title')}>
                        <DateField
                            name="pickupDate"
                            label={translateTab('pickup_date_panel_input_label.pickup_date')}
                            value={pickupDate}
                            placeholder={translateTab('date_field_placeholder.please_enter')}
                            icon={calendarIcon}
                            locale={activeLocale}
                            onChange={value => setPickupDate(value)}
                            onDateLimitError={setIsDateInvalid}
                        />
                    </Panel>
                    <ServiceProviderPanel
                        isEditModeActive={isEditModeActive}
                        selectedServiceProviderId={selectedServiceProviderId}
                        handleOnSelectServiceProvider={handleOnSelectServiceProvider}
                        isPartnerChangingDisabled={isPartnerChangingDisabled}
                    />
                </div>
            </ContentItem>
            <ContentItem className={cx('ace-c-content-item--span-9')}>
                <AlternativeServiceProviderPanel
                    isActive={isEditModeActive}
                    serviceCase={serviceCase}
                    serviceAssignment={serviceAssignment}
                    onToggleSwitch={handleOnToggleSwitch}
                    isPartnerChangingDisabled={isPartnerChangingDisabled}
                />
            </ContentItem>
        </ContentBlock>
    );
};

SASPServiceProviderTab.propTypes = {
    serviceAssignment: PropTypes.object,
    serviceProviders: PropTypes.object,
    submitSASPServiceProviderForm: PropTypes.func.isRequired,
    resetServiceProviderList: PropTypes.func.isRequired,
    serviceCase: PropTypes.object,
};

SASPServiceProviderTab.defaultProps = {
    serviceAssignment: null,
    serviceProviders: null,
    serviceCase: null,
};

const mapStateToProps = (state, props) => {
    const serviceAssignmentSelector = serviceAssignmentSelectors.createServiceAssignmentSelector();
    const serviceCaseSelector = serviceCaseSelectors.createServiceCaseSelector();

    return {
        serviceAssignment: serviceAssignmentSelector(state, props),
        serviceProviders: state.serviceAssignments.serviceProviders,
        serviceCase: serviceCaseSelector(state, props),
    };
};

const mapDispatchToProps = dispatch => ({
    submitSASPServiceProviderForm: payload => dispatch({
        type: saspActionTypes.SUBMIT_SASP_SERVICE_PROVIDER_FORM,
        payload,
    }),
    resetServiceProviderList: () => dispatch({
        type: serviceAssignmentActionTypes.RESET_SERVICE_PROVIDERS,
    }),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SASPServiceProviderTab));
